import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import { useNavigate } from "react-router-dom";
import TrendingUpIcon from "../../../../assets/img/action-center/trending-up.svg";
import ChevronRightSmIcon from "../../../../assets/img/action-center/chevron-right-sm.svg";
import { getReasonCodeLabel } from "../../../../constants/ariReasonCodes";
import { SHE_VIEW_MODE } from "../../../../pages/ari/Ari";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import "./AriCard.scss";

const TOTAL_DAYS = 365;

const AriCard = ({ data = [], onDismiss, cardPosition, hotelName }) => {
  const cardRef = useHeapCardViewed("ari", cardPosition, hotelName);
  const navigate = useNavigate();

  const { availableDays, coveragePercentage, issues } = useMemo(() => {
    let available = 0;
    const issuesList = [];

    data.forEach((item) => {
      if (item.reasonCode === "AVAILABLE") {
        available = item.dayCount;
      } else {
        issuesList.push({
          reasonCode: item.reasonCode,
          label: getReasonCodeLabel(item.reasonCode),
          dayCount: item.dayCount,
        });
      }
    });

    const percentage = Math.round((available / TOTAL_DAYS) * 100);

    return {
      availableDays: available,
      coveragePercentage: percentage,
      issues: issuesList,
    };
  }, [data]);

  const handleReviewAri = () => {
    navigate(`/ari?viewMode=${SHE_VIEW_MODE.MANAGE_ARI}`);
  };

  const handleMaybeLater = () => {
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div ref={cardRef} className="ari-card">
      <div className="ari-card-badge">
        <img src={TrendingUpIcon} alt="" className="badge-icon" />
        <span>Revenue Opportunity</span>
      </div>

      <h3 className="ari-card-title">Review your ARI coverage</h3>

      <p className="ari-card-description">
        Your ARI needs attention &mdash; you may be missing out on bookings.
      </p>

      <div className="ari-card-coverage">
        <div className="coverage-bar">
          <div
            className="coverage-bar-fill"
            style={{ width: `${coveragePercentage}%` }}
            data-testid="coverage-progress-bar"
          />
        </div>
        <div className="coverage-header">
          <span className="coverage-label">Coverage</span>
          <span className="coverage-value">
            {availableDays} / {TOTAL_DAYS} days ({coveragePercentage}%)
          </span>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="ari-card-issues">
          {issues.map((issue) => (
            <span key={issue.reasonCode} className="issue-pill">
              <span className="issue-label">{issue.label}</span>
              <span className="issue-dot" />
              <span className="issue-count">{issue.dayCount} days</span>
            </span>
          ))}
        </div>
      )}

      <div className="ari-card-actions">
        <div className="ari-card-actions-left">
          <Button
            id="action-ari-later"
            appearance="link"
            className="maybe-later-btn"
            onClick={handleMaybeLater}
          >
            Maybe Later
          </Button>
        </div>
        <Button
          id="action-ari-manage"
          appearance="ghost"
          className="review-ari-btn"
          onClick={handleReviewAri}
        >
          Review ARI
          <img src={ChevronRightSmIcon} alt="" className="btn-chevron" />
        </Button>
      </div>
    </div>
  );
};

AriCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      propertyId: PropTypes.number,
      reasonCode: PropTypes.string.isRequired,
      dayCount: PropTypes.number.isRequired,
      runDate: PropTypes.string,
    })
  ).isRequired,
  onDismiss: PropTypes.func,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default AriCard;
